import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot
} from "react-beautiful-dnd";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaNumber from "../../ui-kit/kuika-number/kuika-number";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import { ResolvedColumn } from "./board-columns";
import { currentUserId } from "./current-user";
import InlineCreate from "./inline-create";
import IssueCard, { BoardCardIssue } from "./issue-card";
import { displayNameOf } from "./people-directory";
import StatusGlyph from "./status-glyph";
import {
  IssueCard as IssueCardData,
  IssuePriority,
  LabelDef,
  PRIORITY_COLOR,
  PRIORITY_LABELS,
  PRIORITY_RANK,
  Project,
  Sprint,
  StatusCategory
} from "./types";
import { ColumnFilters } from "./use-column-issues";
import { useColumnSource } from "./use-column-source";

export type ColumnGroupBy = "none" | "assignee" | "priority" | "epic";

// A column's optimistic primitives, registered with the parent for drag-drop + context actions.
export interface ColumnApi {
  removeOptimistic: (id: string) => void;
  insertOptimistic: (card: IssueCardData, index: number) => void;
  patchOptimistic: (id: string, patch: Partial<IssueCardData>) => void;
}

interface EpicLabel {
  label: string;
  color?: string;
}

interface ColumnGroup {
  key: string;
  label: string;
  color?: string;
  items: IssueCardData[];
}

// Group a column's loaded cards. Epic labels are resolved by the parent (it holds the board's
// epic list) via `epicLabel`, since a card carries only its epic id.
function groupCards(
  cards: IssueCardData[],
  groupBy: ColumnGroupBy,
  epicLabel: (epicId: string) => EpicLabel | undefined
): ColumnGroup[] {
  if (groupBy === "none") return [{ key: "all", label: "", items: cards }];
  const buckets: Map<string, ColumnGroup> = new Map();
  const ensure = (key: string, label: string, color?: string): ColumnGroup => {
    let bucket: ColumnGroup | undefined = buckets.get(key);
    if (!bucket) {
      bucket = { key, label, color, items: [] };
      buckets.set(key, bucket);
    }
    return bucket;
  };
  for (const card of cards) {
    if (groupBy === "assignee") {
      const key: string = card.assigneeUserId ?? "__unassigned__";
      ensure(key, card.assigneeUserId ? displayNameOf(card.assigneeUserId) : "Unassigned").items.push(card);
    } else if (groupBy === "priority") {
      ensure(card.priority, PRIORITY_LABELS[card.priority], PRIORITY_COLOR[card.priority]).items.push(card);
    } else {
      const resolved: EpicLabel | undefined = card.epicId ? epicLabel(card.epicId) : undefined;
      if (resolved) ensure(card.epicId!, resolved.label, resolved.color ?? "var(--purple-5)").items.push(card);
      else ensure("__no_epic__", "No epic", "var(--dark-3)").items.push(card);
    }
  }
  const order: ColumnGroup[] = Array.from(buckets.values());
  if (groupBy === "priority") {
    order.sort(
      (a: ColumnGroup, b: ColumnGroup) =>
        (PRIORITY_RANK[a.key as IssuePriority] ?? 99) - (PRIORITY_RANK[b.key as IssuePriority] ?? 99)
    );
  } else {
    order.sort((a: ColumnGroup, b: ColumnGroup) => {
      const sentinel: string = groupBy === "assignee" ? "__unassigned__" : "__no_epic__";
      if (a.key === sentinel) return 1;
      if (b.key === sentinel) return -1;
      return a.label.localeCompare(b.label);
    });
  }
  return order;
}

export interface BoardColumnProps {
  boardId: string;
  column: ResolvedColumn;
  columnIndex: number;
  filters: ColumnFilters;
  groupBy: ColumnGroupBy;
  wipLimit: number | null | undefined;
  focusedIssueId: string | null;
  isColumnFocused: boolean;
  showProjectChip: boolean;
  projects: Project[];
  sprints: Sprint[];
  labelDefs: LabelDef[];
  boardProjects: Project[];
  inlineCreateDefaultProjectKey: string;
  creating: boolean;
  epicLabel: (parentId: string) => EpicLabel | undefined;
  // Register this column's optimistic API with the parent; returns an unregister fn.
  registerApi: (columnId: string, api: ColumnApi) => () => void;
  onData: (columnId: string, items: IssueCardData[], total: number) => void;
  onCardClick: (card: BoardCardIssue) => void;
  onCardContextMenu: (card: BoardCardIssue, x: number, y: number) => void;
  onAddClick: () => void;
  onSetWip: () => void;
  onCreateSubmit: (summary: string, projectKey: string) => void;
  onCreateCancel: () => void;
  // Pointer entered this column — lets the parent track the hovered column (for the "c" shortcut)
  // via a React event instead of a DOM hit-test.
  onHover: () => void;
  // Whether the current user may change board configuration. Gates the manage-only column affordances
  // (drag-to-reorder, click-to-set-WIP); the backend enforces both regardless.
  canManage: boolean;
}

// One board column, backed by useColumnSource — the in-memory local-first store by default
// (zero network on filter/JQL/group changes), falling back to a per-column server query only
// when the working set is too large to sync. Renders header (WIP/total), inline create, the
// droppable card list (grouped) and an infinite-scroll sentinel (server-fallback only). Reports
// its cards + total up via onData so the parent drives keyboard navigation and drag-drop.
const BoardColumn: React.FC<BoardColumnProps> = (props: BoardColumnProps) => {
  const {
    boardId,
    column: col,
    columnIndex,
    filters,
    groupBy,
    wipLimit,
    focusedIssueId,
    isColumnFocused,
    showProjectChip,
    projects,
    sprints,
    labelDefs,
    boardProjects,
    inlineCreateDefaultProjectKey,
    creating,
    epicLabel,
    registerApi,
    onData,
    onCardClick,
    onCardContextMenu,
    onAddClick,
    onSetWip,
    onCreateSubmit,
    onCreateCancel,
    onHover,
    canManage
  } = props;

  // The category color dot reflects the column's first status; the status a card created here gets.
  const dotCategory: StatusCategory = col.statuses[0]?.category ?? "todo";
  const createStatusId: string = col.statusIds[0] ?? "";

  const column = useColumnSource(boardId, col.id, col.statusIds, filters);
  const {
    items,
    total,
    hasMore,
    hasLoaded,
    loadingMore,
    loadMore,
    addOptimistic,
    removeOptimistic,
    insertOptimistic,
    patchOptimistic
  } = column;

  // Expose this column's optimistic move primitives to the parent so a drag-drop can move the
  // card across columns instantly (no snap-back) before the move's reload reconciles.
  useEffect(() => {
    return registerApi(col.id, { removeOptimistic, insertOptimistic, patchOptimistic });
  }, [col.id, registerApi, removeOptimistic, insertOptimistic, patchOptimistic]);
  // Suppress the empty state / cards only until the FIRST load resolves (avoids a "Nothing
  // here yet" flash before data lands). After that they stay shown — an empty column must not
  // flicker its empty state on every mutation's reload.
  const firstLoad: boolean = !hasLoaded;

  // Inline-create: show the card immediately (optimistic) so it doesn't feel like nothing
  // happened, then run the real create — its emit reloads the column with server truth.
  const handleInlineSubmit = (summary: string, projectKey: string): void => {
    addOptimistic({
      id: `optimistic-${col.id}-${Date.now()}`,
      key: "…",
      projectKey,
      type: "task",
      statusId: createStatusId,
      summary,
      priority: "medium",
      assigneeUserId: null,
      // Reporter is stamped server-side from the session; this optimistic placeholder uses the
      // creator's id and is replaced by server truth when the column reloads.
      reporterUserId: currentUserId(),
      labels: [],
      storyPoints: null,
      startDate: null,
      dueDate: null,
      parentIssueId: null,
      epicId: null,
      epicName: null,
      epicColor: null,
      sprintId: null,
      watcherUserIds: [],
      fixVersionId: null,
      rank: "￿",
      createdAt: "",
      updatedAt: "",
      commentCount: 0,
      attachmentCount: 0,
      subtaskCount: 0,
      isBlocked: false,
      archivedAt: null
    });
    onCreateSubmit(summary, projectKey);
  };

  // Report loaded cards + total to the parent whenever they change.
  useEffect(() => {
    onData(col.id, items, total);
  }, [col.id, items, total, onData]);

  // Infinite scroll: load the next page when the sentinel scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel: HTMLDivElement | null = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer: IntersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)) loadMore();
      },
      { root: listRef.current ?? null, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const over: boolean = typeof wipLimit === "number" && total > wipLimit;
  const groups: ColumnGroup[] = groupCards(items, groupBy, epicLabel);
  let runningIndex: number = 0;

  return (
    <Draggable draggableId={`col-${col.id}`} index={columnIndex}>
      {(columnDragProvided: DraggableProvided, columnDragSnapshot: DraggableStateSnapshot) => (
        <div
          ref={columnDragProvided.innerRef}
          {...columnDragProvided.draggableProps}
          className={classNames("kuika-board__column", {
            "kuika-board__column--focused": isColumnFocused,
            "kuika-board__column--dragging": columnDragSnapshot.isDragging
          })}
          role="group"
          aria-label={`${col.name}, ${total} issues`}
          onMouseEnter={onHover}
        >
          <div
            // Drag-to-reorder is a management action — only attach the handle when the user may manage.
            {...(canManage ? (columnDragProvided.dragHandleProps ?? {}) : {})}
            className="kuika-board__column-header"
            aria-label={canManage ? `Reorder column ${col.name}` : col.name}
          >
            <StatusGlyph category={dotCategory} className="kuika-board__column-status-glyph" />
            <span className="kuika-board__column-name">{col.name}</span>
            {canManage ? (
              <span
                className={classNames("kuika-board__column-count", { "kuika-board__column-count--over": over })}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onSetWip();
                }}
                title={wipLimit != null ? `WIP limit: ${wipLimit}` : "Click to set a WIP limit"}
                role="button"
                tabIndex={0}
              >
                <KuikaNumber value={total} />
                {wipLimit != null && (
                  <span className="kuika-board__column-limit">
                    {" / "}
                    <KuikaNumber value={wipLimit} />
                  </span>
                )}
              </span>
            ) : (
              <span
                className={classNames("kuika-board__column-count", { "kuika-board__column-count--over": over })}
                title={wipLimit != null ? `WIP limit: ${wipLimit}` : undefined}
              >
                <KuikaNumber value={total} />
                {wipLimit != null && (
                  <span className="kuika-board__column-limit">
                    {" / "}
                    <KuikaNumber value={wipLimit} />
                  </span>
                )}
              </span>
            )}
            <span className="kuika-board__column-add">
              {/* The shortcut belongs where the action is, not only behind the `?` cheatsheet. */}
              <KuikaTooltip
                content={
                  <span className="kuika-board__tooltip-with-kbd">
                    Create issue
                    <kbd>c</kbd>
                  </span>
                }
                position="bottom"
              >
                <KuikaButton
                  type="tertiary"
                  color="neutral"
                  size="sm"
                  isIcon
                  onClick={onAddClick}
                  data-testid={`board-column-add-${col.id}`}
                >
                  <KuikaSvg name="plus" width={14} height={14} />
                </KuikaButton>
              </KuikaTooltip>
            </span>
          </div>
          <Droppable droppableId={col.id}>
            {(provided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => (
              <div
                ref={(element: HTMLDivElement | null) => {
                  provided.innerRef(element);
                  listRef.current = element;
                }}
                {...provided.droppableProps}
                className={classNames("kuika-board__column-list", {
                  "kuika-board__column-list--draggingOver": dropSnapshot.isDraggingOver
                })}
              >
                {creating && (
                  <InlineCreate
                    projects={boardProjects}
                    defaultProjectKey={inlineCreateDefaultProjectKey}
                    onSubmit={handleInlineSubmit}
                    onCancel={onCreateCancel}
                  />
                )}
                {!firstLoad && total === 0 && !creating && (
                  <div className="kuika-board__column-empty">
                    <div className="kuika-board__column-empty-line">Nothing here yet</div>
                    <div className="kuika-board__column-empty-hint">
                      <span className="kuika-board__column-empty-kbd">c</span>
                      <span>to create</span>
                    </div>
                  </div>
                )}
                {!firstLoad &&
                  groups.map((group: ColumnGroup) => {
                    const elements: React.ReactNode[] = [];
                    if (groupBy !== "none") {
                      elements.push(
                        <div key={`header-${group.key}`} className="kuika-board__group-header">
                          {group.color && (
                            <span className="kuika-board__group-dot" style={{ background: group.color }} />
                          )}
                          <span>{group.label}</span>
                          <span className="kuika-board__group-count">{group.items.length}</span>
                        </div>
                      );
                    }
                    for (const card of group.items) {
                      const index: number = runningIndex;
                      runningIndex += 1;
                      const project: Project | undefined = projects.find(
                        (entry: Project) => entry.key === card.projectKey
                      );
                      const sprintName: string | null = card.sprintId
                        ? (sprints.find((s: Sprint) => s.id === card.sprintId && s.state === "active")?.name ?? null)
                        : null;
                      elements.push(
                        <Draggable key={card.id} draggableId={card.id} index={index}>
                          {(dragProvided: DraggableProvided, dragSnapshot: DraggableStateSnapshot) => (
                            <IssueCard
                              issue={card}
                              focused={focusedIssueId === card.id}
                              dragging={dragSnapshot.isDragging}
                              showProjectChip={showProjectChip}
                              blocked={card.isBlocked}
                              sprintName={sprintName}
                              labelDefs={labelDefs}
                              project={project}
                              statusCategory={dotCategory}
                              onClick={onCardClick}
                              onContextMenu={onCardContextMenu}
                              innerRef={dragProvided.innerRef}
                              draggableProps={dragProvided.draggableProps}
                              dragHandleProps={dragProvided.dragHandleProps ?? undefined}
                            />
                          )}
                        </Draggable>
                      );
                    }
                    return <React.Fragment key={group.key}>{elements}</React.Fragment>;
                  })}
                {provided.placeholder}
                <div ref={sentinelRef} className="kuika-board__column-sentinel" aria-hidden="true" />
                {loadingMore && (
                  <div className="kuika-board__column-loading-more" role="status" aria-label="Loading more issues">
                    <span className="kuika-board__column-loading-bar" />
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default BoardColumn;
